import { useState } from "react";
import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Input,
} from "@reactive-resume/ui";
import {
  DotsThreeVertical,
  Eye,
  EyeSlash,
  PencilSimple,
  TrashSimple,
} from "@phosphor-icons/react";

import { useCoverLetterStore } from "@/client/stores/cover-letter";

type Props = {
  id: string; // customId e.g. "cl-123"
};

export const CLCustomSectionOptions = ({ id }: Props) => {
  const setValue = useCoverLetterStore((state) => state.setValue);
  const removeSection = useCoverLetterStore((state) => state.removeSection);
  const section = useCoverLetterStore((state) => state.coverLetter.data.custom?.[id]);

  const [isRenameOpen, setIsRenameOpen] = useState(false);
  const [name, setName] = useState(section?.name || "Custom Section");

  if (!section) return null;

  const onRename = () => {
    setValue(`custom.${id}.name`, name);
    setIsRenameOpen(false);
  };

  const onToggleVisibility = () => {
    setValue(`custom.${id}.visible`, !section.visible);
  };

  const onDelete = () => {
    removeSection(`custom.${id}`);
  };

  return (
    <>
      <div onClick={(e) => e.stopPropagation()}>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button size="icon" variant="ghost" className="size-8">
              <DotsThreeVertical size={16} />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-48">
            <DropdownMenuItem
              onClick={() => {
                setName(section.name);
                setIsRenameOpen(true);
              }}
            >
              <PencilSimple size={14} className="mr-2" />
              Rename
            </DropdownMenuItem>

            <DropdownMenuItem onClick={onToggleVisibility}>
              {section.visible ? (
                <>
                  <EyeSlash size={14} className="mr-2" />
                  Hide Section
                </>
              ) : (
                <>
                  <Eye size={14} className="mr-2" />
                  Show Section
                </>
              )}
            </DropdownMenuItem>

            <DropdownMenuSeparator />

            <DropdownMenuItem onClick={onDelete} className="text-red-600 focus:text-red-600">
              <TrashSimple size={14} className="mr-2" />
              Delete Section
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      <Dialog open={isRenameOpen} onOpenChange={setIsRenameOpen}>
        <DialogContent onClick={(e) => e.stopPropagation()}>
          <DialogHeader>
            <DialogTitle>Rename Section</DialogTitle>
          </DialogHeader>

          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Section Name"
            autoFocus
            onKeyDown={(e) => {
              if (e.key === "Enter") onRename();
            }}
          />

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsRenameOpen(false)}>
              Cancel
            </Button>
            <Button onClick={onRename}>Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
